/**
 * Password-change domain model: the editable draft and its submit lifecycle.
 *
 * The draft holds two secrets in clear, which is unavoidable: they are the bound
 * values of two controlled inputs. What IS avoidable is letting them outlive the
 * form, so the slice wipes them on close and on unmount (see
 * `password-change.slice.ts`). Do not add a `persist` to this state.
 */

/**
 * The three fields the form edits. `confirmPassword` never reaches the backend
 * (the contract, `ChangePasswordRequestSchema`, carries two fields); it exists so
 * a typo in a masked field is caught here rather than at the next sign-in. The
 * reset-password and onboarding forms ask for the same confirmation; the three
 * places where a password is chosen should not each have their own rules.
 */
export interface PasswordChangeDraft {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const emptyDraft: PasswordChangeDraft = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

/** Per-field validation messages, keyed by draft field. Empty = valid. */
export type PasswordValidationErrors = Partial<Record<keyof PasswordChangeDraft, string>>;

/** The submit lifecycle. `succeeded` is transient; the UI acknowledges it. */
export type SubmitStatus = 'idle' | 'submitting' | 'succeeded' | 'failed';
